import { useCallback, useEffect, useRef, useState } from "react";
import Peer from "simple-peer";
import LiveChat from "./LiveChat";
import FloatingReactions, { ReactionButtons } from "./FloatingReactions";
import { uploadRecording } from "../../functions/live";
import "./live.css";

export default function LiveHost({
  liveId,
  user,
  socket,
  viewerCount,
  chat,
  reactions,
  settings,
  onEnd,
  onChatMessage,
  onReaction,
  dispatch,
}) {
  const [isStreaming, setIsStreaming] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingProgress, setRecordingProgress] = useState(0);
  const [error, setError] = useState(null);
  const [streamTime, setStreamTime] = useState(0);

  const localVideoRef = useRef(null);
  const localStreamRef = useRef(null);
  const viewerPeersRef = useRef(new Map());
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const timerRef = useRef(null);

  // Iniciar stream local
  const startLocalStream = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: "user",
        },
        audio: true,
      });

      localStreamRef.current = stream;
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }

      return stream;
    } catch (err) {
      setError("Não foi possível acessar câmera/microfone: " + err.message);
      return null;
    }
  }, []);

  // Criar peer para novo viewer
  const createPeerForViewer = useCallback((viewerSocketId) => {
    if (!localStreamRef.current) return null;

    const peer = new Peer({
      initiator: true,
      trickle: true,
      stream: localStreamRef.current,
      config: {
        iceServers: [
          { urls: "stun:stun.l.google.com:19302" },
          { urls: "stun:stun1.l.google.com:19302" },
        ],
      },
    });

    peer.on("signal", (signal) => {
      socket.emit("live:signal", {
        liveId,
        targetSocketId: viewerSocketId,
        signal,
      });
    });

    peer.on("error", (err) => {
      console.error("Peer error:", err);
      viewerPeersRef.current.delete(viewerSocketId);
    });

    peer.on("close", () => {
      viewerPeersRef.current.delete(viewerSocketId);
    });

    viewerPeersRef.current.set(viewerSocketId, peer);
    return peer;
  }, [liveId, socket]);

  // Iniciar live
  const handleStartLive = useCallback(async () => {
    const stream = await startLocalStream();
    if (!stream) return;

    socket.emit("live:start", { liveId });
    setIsStreaming(true);

    // Iniciar timer
    timerRef.current = setInterval(() => {
      setStreamTime((prev) => prev + 1);
    }, 1000);
  }, [liveId, socket, startLocalStream]);

  // Encerrar live
  const handleEndLive = useCallback(async () => {
    // Parar gravação se estiver ativa
    if (isRecording && mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
    }

    // Fechar todas as conexões peer
    viewerPeersRef.current.forEach((peer) => {
      peer.destroy();
    });
    viewerPeersRef.current.clear();

    // Parar stream local
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((track) => track.stop());
    }

    // Parar timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    socket.emit("live:end", { liveId });
    setIsStreaming(false);

    if (onEnd) onEnd();
  }, [liveId, socket, isRecording, onEnd]);

  // Iniciar gravação
  const startRecording = useCallback(() => {
    if (!localStreamRef.current) return;

    try {
      const options = {
        mimeType: "video/webm;codecs=vp9",
        videoBitsPerSecond: 2500000,
      };

      const mediaRecorder = new MediaRecorder(localStreamRef.current, options);
      recordedChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const blob = new Blob(recordedChunksRef.current, { type: "video/webm" });

        // Upload para o servidor
        const result = await uploadRecording(
          liveId,
          blob,
          user.token,
          (progress) => setRecordingProgress(progress)
        );

        if (result.status === "ok") {
          console.log("Gravação salva:", result.data.url);
        } else {
          console.error("Erro ao salvar gravação:", result.message);
        }

        setRecordingProgress(0);
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start(10000); // Chunk a cada 10 segundos
      setIsRecording(true);
    } catch (err) {
      setError("Não foi possível iniciar gravação: " + err.message);
    }
  }, [liveId, user.token]);

  // Parar gravação
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  }, [isRecording]);

  // Formatar tempo
  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    }
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Lidar com novo viewer que precisa de stream
  useEffect(() => {
    if (!socket) return;

    const handleViewerNeedsStream = ({ viewerSocketId }) => {
      createPeerForViewer(viewerSocketId);
    };

    const handleSignal = ({ fromSocketId, signal }) => {
      const peer = viewerPeersRef.current.get(fromSocketId);
      if (peer) {
        peer.signal(signal);
      }
    };

    const handleViewerLeft = ({ userId }) => {
      // Peer será removido automaticamente pelo evento close
      console.log("Viewer left:", userId);
    };

    socket.on("live:viewer_needs_stream", handleViewerNeedsStream);
    socket.on("live:signal", handleSignal);
    socket.on("live:viewer_left", handleViewerLeft);

    return () => {
      socket.off("live:viewer_needs_stream", handleViewerNeedsStream);
      socket.off("live:signal", handleSignal);
      socket.off("live:viewer_left", handleViewerLeft);
    };
  }, [socket, createPeerForViewer]);

  // Cleanup ao desmontar
  useEffect(() => {
    return () => {
      if (isStreaming) {
        handleEndLive();
      }
    };
  }, [isStreaming, handleEndLive]);

  return (
    <div className="live_host_container">
      <div className="live_video_section">
        <video
          ref={localVideoRef}
          autoPlay
          muted
          playsInline
          className="live_video"
        />

        {/* Overlay de informações */}
        <div className="live_overlay">
          {isStreaming && (
            <>
              <div className="live_status_bar">
                <div className="live_badge_large">
                  <span className="live_dot"></span>
                  AO VIVO
                </div>
                <div className="live_timer">{formatTime(streamTime)}</div>
                <div className="live_viewers_count">
                  <i className="fa fa-eye"></i>
                  {viewerCount}
                </div>
                {isRecording && (
                  <div className="recording_indicator">
                    <span className="recording_dot"></span>
                    REC
                  </div>
                )}
              </div>

              <FloatingReactions reactions={reactions} />
            </>
          )}

          {error && <div className="live_error">{error}</div>}
        </div>

        {/* Controles */}
        <div className="live_controls">
          {!isStreaming ? (
            <button className="live_start_btn" onClick={handleStartLive}>
              <i className="fa fa-video-camera"></i>
              Iniciar Live
            </button>
          ) : (
            <>
              <button
                className={`live_record_btn ${isRecording ? "recording" : ""}`}
                onClick={isRecording ? stopRecording : startRecording}
              >
                <i className={`fa fa-${isRecording ? "stop" : "circle"}`}></i>
                {isRecording ? "Parar Gravação" : "Gravar"}
              </button>
              <button className="live_end_btn" onClick={handleEndLive}>
                <i className="fa fa-times"></i>
                Encerrar Live
              </button>
            </>
          )}
        </div>

        {recordingProgress > 0 && (
          <div className="upload_progress">
            <div className="progress_bar" style={{ width: `${recordingProgress}%` }}></div>
            <span>Enviando gravação: {recordingProgress}%</span>
          </div>
        )}
      </div>

      <div className="live_sidebar">
        {isStreaming && settings?.reactionsEnabled && (
          <div className="live_reactions_section">
            <ReactionButtons onReact={onReaction} />
          </div>
        )}

        <LiveChat
          chat={chat}
          onSendMessage={onChatMessage}
          disabled={!isStreaming || !settings?.chatEnabled}
          user={user}
        />
      </div>
    </div>
  );
}
